import * as Comlink from "comlink";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { packageRegistryApi } from "services/package-registry-service/PackageRegistryService";
import { sandboxApi } from "services/sandbox-api/api";
import { isFetchBaseQueryError, isValidationErrorDetails } from "services/sandbox-api/helpers";
import { TrackedSandboxEvent, useTrackSandboxEvent } from "services/telemetry-service/telemetry";
import { store } from "store";
import { actionItemsAdded, addResolvedPackages, closeSandbox, imodelSelected, importmapAdded, initTranspiler, sandboxOpened } from "store/features";
import { useAppDispatch } from "store/hooks";
import { CodeShare } from "types";
import { LanguageService } from "@sandbox/code-editor";
import { versionResolverWorker, VersionResolverWorker } from "@sandbox/version-resolver";
import { PlaygroundHandler } from "./PlaygroundHandler";
import Playground from "./Playground";

interface PlaygroundOptions {
  userName: string;
  sandboxName: string;
  viewTestId: string;
}

export const importmap: Record<string, string> = {
  "itwin-v2-importmap-v1": "itwin-v2-importmap-v1-2F9A8212.json",
  "itwin-v3-importmap-v1": "itwin-v3-importmap-v1-EFD26E4C.json",
  "itwin-v4-importmap-v1": "itwin-v4-importmap-v1-10DECDB5.json",
};

export const defaultImportMap = "itwin-v4-importmap-v1";

export const versionResolver = Comlink.wrap<VersionResolverWorker>(versionResolverWorker);

export const PlaygroundLoader = ({ userName, sandboxName, viewTestId }: PlaygroundOptions) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [getSandboxByName] = sandboxApi.useLazyCodeShareResolveQuery();
  const [getImportmap] = packageRegistryApi.useLazyGetImportmapQuery();
  const trackEvent = useTrackSandboxEvent<TrackedSandboxEvent>("Sandbox view");

  const handleError = (error: any) => {
    if (isFetchBaseQueryError(error) && isValidationErrorDetails(error.data)) {
      const data = error.data;
      console.error("sandbox loader error", error);
      if (data.status === 400 && data.title === "One or more validation errors occurred." && data.errors.codeshareName.includes("Could not find Sandbox by name"))
        navigate("/sandbox-not-found", { replace: true, state: { userName, sandboxName } })
    } else {
      console.error("Failed to open sandbox", error);
    }
  }

  const onSandboxOpened = (data?: CodeShare) => {
    if (!data)
      return;

    dispatch(sandboxOpened(data));
    dispatch(actionItemsAdded(["create", "fork", "like", "delete", "shareIdea", "embed", "export"]));
    trackEvent({ SandboxName: data.name, SandboxUser: data.username, SandboxUserId: data.userId, SandboxId: data.id });

    if (data.allowedImodels.length)
      dispatch(imodelSelected(data.allowedImodels[0]));

    let resolvedImportmap = data.attributes.importmap as string;

    if (!resolvedImportmap) {
      if (data.itwinVersion === "4.x") {
        resolvedImportmap = importmap["itwin-v4-importmap-v1"];
      }
      else if (data.itwinVersion === "3.x") {
        resolvedImportmap = importmap["itwin-v3-importmap-v1"];
      }
      else if (data.itwinVersion === "2.x") {
        resolvedImportmap = importmap["itwin-v2-importmap-v1"];
      } else {
        resolvedImportmap = importmap[defaultImportMap];
      }
    } else {
      resolvedImportmap = importmap[resolvedImportmap] ?? importmap[defaultImportMap];
    }

    console.debug(`Loading importmap: ${resolvedImportmap}`);
    getImportmap(resolvedImportmap).unwrap()
      .then((response) => {
        const definitionUrls = response.packages.filter((item) => item.typeDefinitionUrl).map((item) => item.typeDefinitionUrl!)
        LanguageService.addTypeDefinitions(definitionUrls);
        dispatch(addResolvedPackages(response.packages));
        dispatch(importmapAdded(response.importmap));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    const p1 = getSandboxByName({ userName, sandboxName }, true).unwrap();
    const p2 = store.getState().transpiler.esbuildInitialized ? Promise.resolve() : dispatch(initTranspiler()).unwrap();

    Promise.all([p1, p2])
      .then((response) => onSandboxOpened(response[0]))
      .catch((error) => {
        handleError(error);
      })

    return () => {
      dispatch(closeSandbox());
    }
  }, [userName, sandboxName, location.search]);

  return (
    <PlaygroundHandler dataTestId={viewTestId}>
      <Playground />
    </PlaygroundHandler>
  );
}
